<template>
  <DropdownMenu location="bottom" ref="dropdownRef">
    <template #activator>
      <button
        class="MyMapLayerAdditionnalMenu__btn"
        :class="{ 'MyMapLayerAdditionnalMenu__btn--active': isMenuOpen }"
      >
        <Icon
          icon="$dotsHorizontal"
          :color="dotsIconColor"
        />
      </button>
    </template>
    <MenuList class="MyMapLayerPicker__additionnalMenu mt-4">
      <MenuItem>
        <template #prepend>
          <Icon color="main-blue" icon="$shareVariantOutline" />
        </template>
        {{ $t('myMap.rightSidebar.actions.share') }}
      </MenuItem>
      <MenuItem @click="downloadSourceData">
        <template #prepend>
          <Icon color="main-blue" icon="$downloadOutline" />
        </template>
        {{ $t('myMap.rightSidebar.actions.downloadLayer') }}
      </MenuItem>
      <MenuItem
        @click="
          (event) => {
            event.stopPropagation()
            toggleOpacity()
          }
        "
      >
        <template #prepend>
          <Icon color="main-blue" icon="$opacity" />
        </template>
        {{ $t('myMap.rightSidebar.actions.opacity') }}
      </MenuItem>
      <MyMapLayerOpacityPicker
        v-if="isLayerOpacityShown && mainLayer"
        v-model="mainLayer.opacity"
        @click.prevent.stop
      />
      <MenuItem
        v-if="loadedLayerType === LayerType.OSM_LAYER && isHeatmapEligible"
        @click="onHeatmapMenuClick"
      >
        <template #prepend>
          <Icon
            icon="mdi:chart-scatter-plot"
            :color="isHeatmapActive ? 'main-blue' : 'dark-grey'"
          />
        </template>
        {{ $t('myMap.rightSidebar.actions.heatmap') }}
      </MenuItem>
    </MenuList>
  </DropdownMenu>
</template>
<script setup lang="ts">
import DropdownMenu from '@/components/ui/DropdownMenu.vue'
import Icon from '@/components/ui/Icon.vue'
import MenuItem from '@/components/ui/MenuItem.vue'
import MenuList from '@/components/ui/MenuList.vue'
import { NotificationType } from '@/models/enums/app/NotificationType'
import { LayerType } from '@/models/enums/geo/LayerType'
import type { Layer } from '@/models/interfaces/map/Layer'
import { i18n } from '@/plugins/i18n'
import { addNotification } from '@/services/notifications/NotificationService'
import { downloadJson } from '@/services/utils/UtilsService'
import { useMyMapStore } from '@/stores/myMapStore'
import { computed, ref, watch, type ModelRef } from 'vue'
import MyMapLayerOpacityPicker from './MyMapLayerOpacityPicker.vue'

const isLayerOpacityShown: ModelRef<boolean | undefined> = defineModel('isLayerOpacityShown', {
  default: false,
})
const mainLayer: ModelRef<Layer | undefined> = defineModel('mainLayer')
const myMapStore = useMyMapStore()
const props = withDefaults(
  defineProps<{
    loadedLayerType?: LayerType
    /** Domaine-style platform row: keep overflow icon teal like the title. */
    activatorAlwaysMainBlue?: boolean
  }>(),
  {
    loadedLayerType: undefined,
    activatorAlwaysMainBlue: false,
  },
)

const dropdownRef = ref<InstanceType<typeof DropdownMenu> | null>(null)
const isMenuOpen = ref(false)

const dotsIconColor = computed(() => {
  if (props.activatorAlwaysMainBlue) return 'main-blue'
  return isMenuOpen.value ? 'main-blue' : 'dark-grey'
})

const toggleOpacity = () => {
  isLayerOpacityShown.value = !isLayerOpacityShown.value
}

watch(
  () => dropdownRef.value?.isOpen,
  (isOpen) => {
    isMenuOpen.value = isOpen ?? false
    if (!isOpen) {
      isLayerOpacityShown.value = !!mainLayer.value?.opacity && mainLayer.value?.opacity < 100
    }
  },
)

const styleIdStr = computed(() => mainLayer.value?.id?.toString() ?? '')

const isHeatmapEligible = computed(() =>
  myMapStore.osmClusteredStyleIds.includes(styleIdStr.value),
)

const isHeatmapActive = computed(() => myMapStore.osmHeatmapStyleIds.has(styleIdStr.value))

function onHeatmapMenuClick(event: MouseEvent): void {
  event.stopPropagation()
  const id = mainLayer.value?.id?.toString()
  if (id) myMapStore.toggleOsmHeatmap(id)
}

const downloadSourceData = async () => {
  const layerId = mainLayer.value?.id.toString()
  if (myMapStore.myMap?.map && layerId) {
    if (props.loadedLayerType === LayerType.APP_LAYER) {
      const data = await myMapStore.myMap?.getData(layerId)
      if (data) {
        downloadJson(data, layerId)
      }
    } else {
      // QGIS functionality removed
      addNotification(i18n.t('myMap.atlases.dataNotFetchable'), NotificationType.ERROR)
    }
  }
}
</script>

<style scoped>
.MyMapLayerAdditionnalMenu__btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.MyMapLayerAdditionnalMenu__btn:hover {
  /*  opacity: 0.7; */
  background-color: var(--color-main-hover);
}

.MyMapLayerAdditionnalMenu__btn:focus,
.MyMapLayerAdditionnalMenu__btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-main-blue));
  outline-offset: 2px;
}
</style>
